import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = articles
    .filter((a) => a.category === slug)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (!currentCategory) {
    return (
      <>
        <SEOHead title="分类未找到" description="抱歉，您访问的分类不存在。" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={currentCategory.name}
        description={`友田真希${currentCategory.description}。${currentCategory.name}相关的最新内容、资讯与信息。`}
        keywords={`友田真希,${currentCategory.name},友田真希 作品,友田真希 艺能活动`}
        canonicalUrl={`https://huanyung.cn/category/${slug}`}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm">{currentCategory.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`友田真希 ${article.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 whitespace-nowrap">
                        {currentCategory.name}
                      </span>
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h2 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <p className="text-xs text-foreground-500 mt-3">
                      阅读时间 {article.readTime} 分钟
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}